import React from 'react';
import styled from 'styled-components';

import type { DomainShareScope } from '../../../types/api';
import type { ShareScopeConfig } from '../lib/shareScopes';

const Card = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
  padding: var(--space-md);
  border: 2px solid
    ${(props) =>
      props.$selected ? 'var(--brand-600)' : 'var(--border, #e5e7eb)'};
  border-radius: var(--radius-md, 8px);
  background: ${(props) =>
    props.$selected ? 'var(--brand-50, #eef2ff)' : 'var(--surface, #fff)'};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  transition:
    border-color 120ms ease,
    background-color 120ms ease;

  &:hover {
    border-color: ${(props) =>
      props.$disabled ? 'var(--border, #e5e7eb)' : 'var(--brand-600)'};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.span<{ $color: string }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${(props) => props.$color};
`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span`
  color: var(--text-primary);
  font-weight: 600;
`;

const Description = styled.span`
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
`;

const Count = styled.span`
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

export interface ShareScopeOptionCardProps {
  config: ShareScopeConfig;
  selected: boolean;
  /** Undefined until the count for this scope has loaded. */
  fileCount?: number | undefined;
  onSelect: (scope: DomainShareScope) => void;
}

export const ShareScopeOptionCard: React.FC<ShareScopeOptionCardProps> = ({
  config,
  selected,
  fileCount,
  onSelect,
}) => {
  const Icon = config.icon;
  const isEmpty = fileCount === 0;

  const handleSelect = (): void => {
    if (isEmpty) return;
    onSelect(config.scope);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleSelect();
    }
  };

  return (
    <Card
      role="radio"
      aria-checked={selected}
      aria-disabled={isEmpty}
      // Roving tabindex: only the selected card is in the tab order, and arrow keys
      // move between options (handled by the parent radiogroup).
      tabIndex={selected ? 0 : -1}
      data-scope={config.scope}
      $selected={selected}
      $disabled={isEmpty}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <IconWrapper $color={config.color} aria-hidden="true">
        <Icon size={22} />
      </IconWrapper>
      <Body>
        <Title>{config.title}</Title>
        <Description>{config.description}</Description>
        {fileCount !== undefined && (
          <Count>
            {isEmpty
              ? config.emptyLabel
              : `${fileCount} ${fileCount === 1 ? 'archivo' : 'archivos'}`}
          </Count>
        )}
      </Body>
    </Card>
  );
};
